import type { Language } from '../../i18n';
import { useTranslation } from '../../hooks/useTranslation';

interface ShopTabProps {
  rewards: any[];
  userPoints: number;
  language: Language;
  formatPoints: (points: number) => string;
  onRedeem: (rewardId: string) => void;
  noRewardsText: string;
}

const ShopTab = ({ rewards, userPoints, formatPoints, onRedeem, noRewardsText }: ShopTabProps) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {rewards.length > 0 ? (
        rewards.map((reward) => {
          const canAfford = userPoints >= reward.pointsRequired;
          const isAvailable = reward.available && (reward.stock === -1 || reward.stock > 0);
          
          return (
            <div
              key={reward.id}
              className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${
                canAfford && isAvailable
                  ? 'hover:scale-105'
                  : 'opacity-60'
              }`}
            >
              {/* Gradient Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-yellow-500/5 to-primary/10" />
              <div className={`absolute inset-0 ${
                canAfford && isAvailable
                  ? 'bg-gradient-to-r from-transparent via-primary/5 to-transparent animate-pulse'
                  : ''
              }`} />
              
              <div className="relative bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-xl p-5 border border-gray-700/50">
                <div className="flex items-start gap-4 mb-4">
                  {/* Image or Icon */}
                  <div className="flex-shrink-0">
                    {reward.image ? (
                      <img 
                        src={reward.image} 
                        alt={reward.name}
                        className="w-16 h-16 rounded-2xl object-cover border-2 border-primary/30"
                        onError={(e) => {
                          // Fallback to icon if image fails to load
                          (e.target as HTMLImageElement).style.display = 'none';
                          const parent = (e.target as HTMLImageElement).parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center shadow-lg">
                                <svg class="w-9 h-9 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
                                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                                </svg>
                              </div>
                            `;
                          }
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center shadow-lg">
                        <svg className="w-9 h-9 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-1">
                      {reward.name}
                    </h3>
                    
                    <p className="text-sm text-gray-400 mb-3">
                      {reward.description}
                    </p>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/20 rounded-lg border border-primary/30">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                        <span className="text-sm font-bold text-primary">
                          {formatPoints(reward.pointsRequired)} {t('checkIn.points')}
                        </span>
                      </div>
                      
                      {reward.stock !== -1 && (
                        <div className="text-xs text-gray-500">
                          Stock: {reward.stock}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => onRedeem(reward.id)}
                  disabled={!canAfford || !isAvailable}
                  className={`w-full py-3.5 rounded-xl font-bold text-base transition-all duration-300 flex items-center justify-center gap-2 ${
                    canAfford && isAvailable
                      ? 'bg-gradient-to-r from-primary via-yellow-400 to-primary text-black hover:scale-105 active:scale-95'
                      : 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {!isAvailable ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      {t('common.error')}
                    </>
                  ) : !canAfford ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {t('checkIn.insufficientPoints')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                      {t('checkIn.redeemNow')}
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })
      ) : (
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-12 text-center border border-gray-700/50">
          <div className="w-20 h-20 mx-auto mb-4 bg-gray-700/30 rounded-2xl flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <p className="text-gray-400">{noRewardsText}</p>
        </div>
      )}
    </div>
  );
};

export default ShopTab;
